'use client';

import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { createClient, resetClient } from '@/lib/supabase/client';
import { sendSignupConfirmationEmail,  } from '@/lib/services/emailService';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const clearAllAuthStorage = () => {
  // Clear localStorage
  try {
    Object.keys(localStorage)
      .filter((k) => k.includes('supabase') || k.includes('sb-'))
      .forEach((k) => localStorage.removeItem(k));
  } catch {}
  // Clear sessionStorage
  try {
    Object.keys(sessionStorage)
      .filter((k) => k.includes('supabase') || k.includes('sb-'))
      .forEach((k) => sessionStorage.removeItem(k));
  } catch {}
  // Clear cookies
  try {
    document.cookie.split(';').forEach((c) => {
      const name = c.trim().split('=')[0];
      if (name.includes('sb-') || name.includes('supabase')) {
        document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=None; Secure`;
        document.cookie = `${name}=; Path=/; Max-Age=0`;
      }
    });
  } catch {}
  // Reset the singleton client so the next createClient() returns a fresh instance
  resetClient();
};

const isStaleTokenError = (msg: string, code?: string) =>
  msg.includes('Refresh Token Not Found') ||
  msg.includes('Invalid Refresh Token') ||
  msg.includes('refresh_token_not_found') ||
  code === 'refresh_token_not_found';

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  // Keep supabase client in a ref so it's stable across renders
  const supabaseRef = useRef(createClient());
  const supabase = supabaseRef.current;

  const fetchProfile = async (userId: string) => {
    setProfileLoading(true);
    try {
      const { data } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      setProfile(data);
    } catch {
      // ignore
    } finally {
      setProfileLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;

    const initAuth = async () => {
      // Check for stale tokens before subscribing to auth state changes
      try {
        const { data: sessionData, error } = await supabase.auth.getSession();
        if (error && isStaleTokenError(error.message ?? '', (error as any).code)) {
          clearAllAuthStorage();
          // Reinitialize supabase ref with fresh client after reset
          supabaseRef.current = createClient();
          if (mounted) {
            setSession(null);
            setUser(null);
            setProfile(null);
            setLoading(false);
          }
          return;
        }
        // If session exists but has a stale refresh token indicator, clear it
        if (sessionData?.session) {
          const refreshToken = sessionData.session.refresh_token;
          if (!refreshToken) {
            clearAllAuthStorage();
            // Reinitialize supabase ref with fresh client after reset
            supabaseRef.current = createClient();
            if (mounted) {
              setSession(null);
              setUser(null);
              setProfile(null);
              setLoading(false);
            }
            return;
          }
        }
      } catch {
        // Network error — proceed normally
      }

      if (!mounted) return;

      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((event, currentSession) => {
        if (!mounted) return;

        if (event === 'TOKEN_REFRESH_FAILED') {
          clearAllAuthStorage();
          setSession(null);
          setUser(null);
          setProfile(null);
          setLoading(false);
          supabase.auth.signOut({ scope: 'local' }).catch(() => {});
          return;
        }

        if (event === 'SIGNED_OUT') {
          clearAllAuthStorage();
          setSession(null);
          setUser(null);
          setProfile(null);
          setLoading(false);
          return;
        }

        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        if (currentSession?.user) {
          fetchProfile(currentSession.user.id);
        } else {
          setProfile(null);
        }
        setLoading(false);
      });

      return () => {
        subscription.unsubscribe();
      };
    };

    let cleanupFn: (() => void) | undefined;
    initAuth().then((fn) => {
      cleanupFn = fn;
    });

    return () => {
      mounted = false;
      cleanupFn?.();
    };
  }, []);

  // Email/Password Sign Up
  const signUp = async (email: string, password: string, metadata: any = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          avatar_url: metadata?.avatarUrl || '',
          phone: metadata?.phone || '',
          role: 'client',
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });
    if (error) throw error;

    if (data.user) {
      const fullName = metadata?.fullName || email.split('@')[0];

      sendSignupConfirmationEmail({
        to: email,
        fullName,
        confirmationUrl: `${window.location.origin}/auth/callback`,
      });
    }

    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) throw error;
    return data;
  };

  // Sign Out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
  };

  // Get Current User
  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database
  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  // Reset Password
  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/callback?next=/reset-password`,
    });
    if (error) throw error;
  };

  const isAdmin = profile?.role === 'admin' ||
    user?.user_metadata?.role === 'admin' ||
    user?.app_metadata?.role === 'admin';

  const value = {
    user,
    session,
    profile,
    loading,
    profileLoading,
    isAdmin,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
